import { Component, computed, effect, inject, input, output, signal, viewChild } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  getCreateTimerecorddetailDtoFormGroup,
  getUpdateTimerecorddetailDtoFormGroup,
  WorkCreateTimerecorddetailDto,
  WorkMontageplanBlockDto,
  WorkMontageplanV2Service,
  WorkSearchCategoryEnum,
  WorkTimeBlockTemplateEntry,
  WorkTimeBlockTemplatesV2Service,
  WorkTimerecord,
  WorkTimerecorddetail,
  WorkTimerecorddetailsV2Service,
  WorkUpdateTimerecorddetailDto,
} from '@work-frontend-monorepo/work-rest-api';
import { lastValueFrom } from 'rxjs';
import dayjs from 'dayjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { GlobalMegasearchInputComponent } from '../../../../../../../work-office/src/app/global/components/global-megasearch-input/global-megasearch-input.component';
import {ButtonComponent} from '@work-frontend-monorepo/work-ui/layout';
import { AlertDialogService, ModalComponent } from '@work-frontend-monorepo/work-ui/modals';
import { InputFieldComponent, SelectComponent, SelectDropdownOptionsApiService } from '@work-frontend-monorepo/work-ui/forms';
import { ExceptionHandlingService } from '@work-frontend-monorepo/work-ui/services';
import { getCreateUpdateTimerecorddetailPayload } from '@work-frontend-monorepo/work-ui/utilities';
import { TIMERECORDTYPE_ID_ARBEIT } from '../../functions/arbeitszeit-timerecordtypes.const';

@Component({
  selector: 'sfg-app-arbeitszeit-create-update-modal',
  templateUrl: './arbeitszeit-create-update-modal.component.html',
  styleUrls: ['./arbeitszeit-create-update-modal.component.scss'],
  standalone: true,
  imports: [
    ButtonComponent,
    ModalComponent,
    ReactiveFormsModule,
    TranslatePipe,
    InputFieldComponent,
    SelectComponent,
    AsyncPipe,
    GlobalMegasearchInputComponent,
    NgTemplateOutlet,
  ],
})
export class ArbeitszeitCreateUpdateModalComponent {
  // Inputs
  timerecord = input<WorkTimerecord | undefined>();
  timerecorddetailToUpdate = input<WorkTimerecorddetail>();
  // Outputs
  saved = output<void>();
  // Signals and State
  modal = viewChild<ModalComponent>('arbeitszeitCreateUpdateModal');
  timerecorddetails = signal<WorkTimerecorddetail[]>([]);
  timeBlockSuggestions = signal<WorkTimeBlockTemplateEntry[]>([]);
  projectSuggestions = signal<WorkMontageplanBlockDto[]>([]);
  projectSearchTerm = signal<string>('');
  selectedSuggestions = signal<Set<number>>(new Set());
  formgroupCreate = getCreateTimerecorddetailDtoFormGroup();
  formgroupUpdate = getUpdateTimerecorddetailDtoFormGroup();
  protected readonly WorkSearchCategoryEnum = WorkSearchCategoryEnum;
  readonly noteSuggestions = ['Montage', 'Demontage', 'Korrektur Gerüst', 'Kontrolle'];

  // Im Create-Modus: Speichern nur erlaubt wenn mind. 1 Suggestion selektiert (oder keine Suggestions vorhanden)
  canSaveCreate = computed(() => {
    const suggestions = this.timeBlockSuggestions();
    if (suggestions.length === 0) return true;
    return this.selectedSuggestions().size > 0;
  });

  // Computed: Prüft ob eine Suggestion bereits als Timerecorddetail existiert
  isSuggestionAlreadyUsed = computed(() => {
    const timerecord = this.timerecord();
    const existingDetails = timerecord?.timerecorddetails || [];

    return (suggestion: WorkTimeBlockTemplateEntry): boolean => {
      return existingDetails.some(
        (detail) =>
          detail.timeStart === suggestion.timeStart &&
          detail.timeEnd === suggestion.timeEnd &&
          detail.timerecordtypeId === suggestion.timerecordtypeId,
      );
    };
  });

  // Computed: Set von projectIds, die für das aktuelle Datum eingeplant sind
  projectsPlannedForToday = computed(() => {
    const timerecord = this.timerecord();
    const projects = this.projectSuggestions();

    if (!timerecord?.workdate) return new Set<number>();

    const workdate = dayjs(timerecord.workdate);
    const plannedProjectIds = new Set<number>();

    projects.forEach(block => {
      const startDate = dayjs(block.startDate);
      const endDate = dayjs(block.endDate);

      if (workdate.isBetween(startDate, endDate, 'day', '[]')) {
        plannedProjectIds.add(block.projectId);
      }
    });

    return plannedProjectIds;
  });
  // Injections
  private readonly selectDropdownOptionsService = inject(SelectDropdownOptionsApiService);
  timerecordtypeDropdownOptions = computed(async () => {
    const timerecordtypes = await lastValueFrom(this.selectDropdownOptionsService.timerecordtypes$);
    return timerecordtypes.filter((timerecordtype) => ['Arbeit', 'Kompensation'].indexOf(timerecordtype.label) > -1);
  });
  private readonly exceptionHandlingService = inject(ExceptionHandlingService);
  private readonly workTimerecorddetailsV2Service = inject(WorkTimerecorddetailsV2Service);
  private readonly workTimeBlockTemplatesV2Service = inject(WorkTimeBlockTemplatesV2Service);
  private readonly workMontageplanV2Service = inject(WorkMontageplanV2Service);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly translateService = inject(TranslateService);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private timerecorddetailToUpdateEffect = effect(() => {
    const timerecorddetailToUpdate = this.timerecorddetailToUpdate();
    if (timerecorddetailToUpdate) {
      const { locked, verified, timeStart, timeEnd, ...updateDto } = timerecorddetailToUpdate;
      // Konvertiere HH:mm:ss zu HH:mm für HTML time input
      this.formgroupUpdate.patchValue({
        ...updateDto,
        // Fallback auf 'Arbeit', falls der Eintrag keinen Typ gesetzt hat (nullable in der DB) —
        // sonst schlägt die required-Validierung fehl und der Speichern-Button bleibt deaktiviert
        timerecordtypeId: updateDto.timerecordtypeId ?? TIMERECORDTYPE_ID_ARBEIT,
        timeStart: timeStart?.substring(0, 5),
        timeEnd: timeEnd?.substring(0, 5),
      });
      // Setze projectSearchTerm für die Megasearch
      this.projectSearchTerm.set(timerecorddetailToUpdate.project?.numberQualified ?? '');
    } else {
      this.formgroupUpdate = getUpdateTimerecorddetailDtoFormGroup();
      this.projectSearchTerm.set('');
    }
  });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private timerecordEffect = effect(() => {
    const timerecord = this.timerecord();
    if (timerecord) {
      this.formgroupCreate.patchValue({ timerecordId: timerecord.id, timerecord: timerecord, timerecordtypeId: TIMERECORDTYPE_ID_ARBEIT });
      this.formgroupUpdate.patchValue({ timerecordId: timerecord.id, timerecord: timerecord });

      // Lade TimeBlock-Suggestions für den User und das Datum
      if (timerecord.userId && timerecord.workdate) {
        this.loadTimeBlockSuggestions(timerecord.userId, timerecord.workdate);
        this.loadProjectSuggestions(timerecord.userId, timerecord.workdate);
      }
    }
  });

  private async loadTimeBlockSuggestions(userId: number, date: string) {
    try {
      const suggestions = await lastValueFrom(
        this.workTimeBlockTemplatesV2Service.timeBlockTemplatesV2ControllerGetUserTimeBlockSuggestions({
          userId,
          date,
          withAutoCreated: false,
        }),
      );
      this.timeBlockSuggestions.set(suggestions);
    } catch (error) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadTimeBlockSuggestionsException', error);
    }
  }

  private async loadProjectSuggestions(userId: number, date: string) {
    try {
      // Berechne Start und Ende der aktuellen Woche
      const currentDate = dayjs(date);
      const fromDate = currentDate.startOf('isoWeek').format('YYYY-MM-DD');
      const toDate = currentDate.endOf('isoWeek').format('YYYY-MM-DD');

      const montageplanDays = await lastValueFrom(
        this.workMontageplanV2Service.montageplanV2ControllerGetMontageplanBlocksByUserIdAndDaterange({
          userId,
          fromDate,
          toDate,
        }),
      );

      // Extrahiere alle Blocks aus allen Teams und Tagen
      const allBlocks: WorkMontageplanBlockDto[] = [];
      montageplanDays.forEach((day) => {
        day.teams.forEach((team) => {
          allBlocks.push(...team.blocks);
        });
      });

      // Entferne Duplikate basierend auf projectId
      const uniqueBlocks = allBlocks.reduce((acc, block) => {
        if (!acc.find((b) => b.projectId === block.projectId)) {
          acc.push(block);
        }
        return acc;
      }, [] as WorkMontageplanBlockDto[]);

      this.projectSuggestions.set(uniqueBlocks);

      if (!this.timerecorddetailToUpdate()) {
        const todayProjectIds = this.projectsPlannedForToday();
        const todayBlock = uniqueBlocks.find((block) => todayProjectIds.has(block.projectId));
        if (todayBlock) {
          this.applyProjectSuggestion(todayBlock);
        }
      }
    } catch (error) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadProjectSuggestionsException', error);
    }
  }


  protected toggleSuggestionSelection(suggestion: WorkTimeBlockTemplateEntry) {
    const current = new Set(this.selectedSuggestions());
    if (current.has(suggestion.id)) {
      current.delete(suggestion.id);
    } else {
      current.add(suggestion.id);
    }
    this.selectedSuggestions.set(current);

    // Von/Bis-Felder befüllen wenn genau 1 Suggestion selektiert
    const allSuggestions = this.timeBlockSuggestions();
    const selectedEntries = allSuggestions.filter((s) => current.has(s.id));
    if (selectedEntries.length === 1) {
      this.formgroupCreate.patchValue({
        timeStart: selectedEntries[0].timeStart?.substring(0, 5),
        timeEnd: selectedEntries[0].timeEnd?.substring(0, 5),
        timerecordtypeId: selectedEntries[0].timerecordtypeId,
      });
    } else {
      // Bei 0 oder 2+ Selektionen die Zeit-Felder leeren
      this.formgroupCreate.patchValue({
        timeStart: '',
        timeEnd: '',
      });
    }
  }

  protected isSuggestionSelected(suggestion: WorkTimeBlockTemplateEntry): boolean {
    return this.selectedSuggestions().has(suggestion.id);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected applyNoteSuggestion(formgroup: FormGroup<any>, note: string) {
    formgroup.patchValue({ notes: note });
  }

  protected applyProjectSuggestion(block: WorkMontageplanBlockDto) {
    const formgroup = this.timerecorddetailToUpdate() ? this.formgroupUpdate : this.formgroupCreate;
    formgroup.patchValue({
      projectId: block.projectId,
    });
    this.projectSearchTerm.set(`${block.projectName}, ${block.projectStreet}, ${block.projectZipCode} ${block.projectCity}`);
  }

  protected applySuggestion(suggestion: WorkTimeBlockTemplateEntry) {
    const formgroup = this.timerecorddetailToUpdate() ? this.formgroupUpdate : this.formgroupCreate;
    // Konvertiere HH:mm:ss zu HH:mm für HTML time input
    const timeStart = suggestion.timeStart?.substring(0, 5);
    const timeEnd = suggestion.timeEnd?.substring(0, 5);
    formgroup.patchValue({
      timeStart,
      timeEnd,
      timerecordtypeId: suggestion.timerecordtypeId,
    });
  }

  protected async save() {
    try {
      const timerecorddetailToUpdate = this.timerecorddetailToUpdate();
      if (timerecorddetailToUpdate) {
        await lastValueFrom(
          this.workTimerecorddetailsV2Service.timerecorddetailsV2ControllerUpdate({
            id: timerecorddetailToUpdate.id,
            body: getCreateUpdateTimerecorddetailPayload(this.formgroupUpdate.value as WorkUpdateTimerecorddetailDto),
          }),
        );
      } else {
        const baseDto = getCreateUpdateTimerecorddetailPayload(this.formgroupCreate.value as WorkCreateTimerecorddetailDto);
        const selectedIds = this.selectedSuggestions();
        const suggestions = this.timeBlockSuggestions();
        const selectedEntries = suggestions.filter((s) => selectedIds.has(s.id));

        if (selectedEntries.length > 0) {
          // Erstelle einen Eintrag pro selektierter Zeitblock-Suggestion
          for (const suggestion of selectedEntries) {
            await lastValueFrom(
              this.workTimerecorddetailsV2Service.timerecorddetailsV2ControllerCreate({
                body: {
                  ...baseDto,
                  timeStart: suggestion.timeStart,
                  timeEnd: suggestion.timeEnd,
                  timerecordtypeId: suggestion.timerecordtypeId,
                },
              }),
            );
          }
        } else {
          await lastValueFrom(
            this.workTimerecorddetailsV2Service.timerecorddetailsV2ControllerCreate({
              body: baseDto,
            }),
          );
        }
      }
      this.alertDialogService.showAlert({
        title: this.translateService.instant('AENDERUNG_SPEICHERN_ERFOLGREICH'),
        feedbackState: 'success',
      });
      this.modal()?.close();
      this.resetForms();
      this.saved.emit();
    } catch (timerecorddetailSaveException) {
      this.exceptionHandlingService.handleExceptionWithAlert('timerecorddetailSaveException', timerecorddetailSaveException);
    }
  }

  private resetForms() {
    this.formgroupCreate = getCreateTimerecorddetailDtoFormGroup();
    this.formgroupUpdate = getUpdateTimerecorddetailDtoFormGroup();
    this.selectedSuggestions.set(new Set());
    this.projectSearchTerm.set('');
  }
}
